import { useMemo } from 'react';
import { Checkbox, useField } from '@lingoda/forms';
import { Box, Typography } from '@lingoda/ui';
import { trans } from '@lingoda/i18n';
import { TermsLinkSprint } from '@lingoda/legal';
import { AgreementsForm } from './AgreementsForm';
import { Agreements } from './Agreements';
import { type CheckoutAgreement, type CheckoutAgreements } from './types';

interface AgreementsSprintRouterProps {
    agreements: CheckoutAgreements;
}

export const AgreementsSprintRouter = ({ agreements }: AgreementsSprintRouterProps) => {
    const foundSprintAgreements = useMemo(() => {
        const sprintAgreementIndex = agreements.findIndex((item) => item.pdfLink);

        if (sprintAgreementIndex < 0) {
            return undefined;
        }

        const sprintAgreement = agreements[sprintAgreementIndex];

        const otherAgreements = [
            ...agreements.slice(0, sprintAgreementIndex),
            ...agreements.slice(sprintAgreementIndex + 1),
        ];

        return { sprint: sprintAgreement, rest: otherAgreements };
    }, [agreements]);

    if (foundSprintAgreements) {
        return (
            <AgreementsSprint
                sprintAgreement={foundSprintAgreements.sprint}
                agreements={foundSprintAgreements.rest}
            />
        );
    }

    return <Agreements agreements={agreements} />;
};

interface AgreementsSprintProps {
    sprintAgreement: CheckoutAgreement;
    agreements: CheckoutAgreements;
}

const AgreementsSprint = ({ agreements, sprintAgreement }: AgreementsSprintProps) => {
    const [, , sprintAgreementField] = useField(`agreements.${sprintAgreement.id}`);

    return (
        <Box>
            <Checkbox
                name="termsAndConditions"
                label={<TermsLinkSprint pdfLink={sprintAgreement.pdfLink || ''} />}
                checkboxProps={{
                    onChange: (_, value) => {
                        void sprintAgreementField.setValue(value);
                        // The reason behind `setTimeout` https://github.com/jaredpalmer/formik/issues/2059#issuecomment-639960398
                        setTimeout(() => sprintAgreementField.setTouched(true));
                    },
                }}
            />
            {agreements.length > 0 && <AgreementsForm agreements={agreements} />}
            <Checkbox
                name="ageAboveEighteen"
                label={
                    <Typography variant="body2">
                        {trans('i-confirm-i-am-above-eighteen-years-old', {}, 'public-payment')}
                    </Typography>
                }
            />
        </Box>
    );
};
